import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Workflow, ChevronRight, ArrowRight, Layers, Clock, Zap, Plus
} from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProcessRegistry = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/appsupport/registry/workflows`);
      const d = await res.json();
      setWorkflows(d.workflows || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const seedTemplates = async () => {
    await fetch(`${BACKEND_URL}/api/appsupport/registry/seed-templates`, { method: 'POST' });
    fetchWorkflows();
  };

  useEffect(() => { fetchWorkflows(); }, []);

  const openWorkflow = (wf) => {
    setSelectedWorkflow(wf);
    setSheetOpen(true);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="process-registry">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <PageHeader title="Business Process Registry" subtitle="Workflow templates and definitions" onRefresh={fetchWorkflows}>
          <Button size="sm" onClick={seedTemplates} variant="outline" className="gap-1.5" data-testid="business-process-seed-button">
            <Layers className="h-3.5 w-3.5" /> Seed Templates
          </Button>
        </PageHeader>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-48" />)}
          </div>
        ) : workflows.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Workflow className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium">No workflow templates</p>
              <p className="text-sm text-muted-foreground mt-1">Seed templates for Energy/Utilities/Trading</p>
              <Button onClick={seedTemplates} className="mt-4" data-testid="business-process-create-template-button">
                <Plus className="h-4 w-4 mr-2" /> Seed Templates
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflows.map((wf) => (
              <Card key={wf.workflow_id} className="cursor-pointer hover:border-[hsl(var(--ring))]/50 transition-colors duration-150" onClick={() => openWorkflow(wf)} data-testid="workflow-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Workflow className="h-4 w-4 text-[hsl(var(--ring))]" />
                      {wf.name}
                    </CardTitle>
                    {wf.is_template && <Badge variant="secondary" className="text-xs">Template</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">{wf.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {wf.steps?.length || 0} steps</span>
                    <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> v{wf.version}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {wf.steps?.slice(0, 3).map((step) => (
                      <Badge key={step.step_number} variant="outline" className="text-xs font-mono">
                        {step.business_name}
                      </Badge>
                    ))}
                    {(wf.steps?.length || 0) > 3 && <Badge variant="outline" className="text-xs">+{wf.steps.length - 3}</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Workflow Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          {selectedWorkflow && (
            <>
              <SheetHeader>
                <SheetTitle style={{ fontFamily: 'var(--font-display)' }}>{selectedWorkflow.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">{selectedWorkflow.description}</p>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">{selectedWorkflow.vertical?.replace(/_/g, ' ')}</Badge>
                  <span className="text-xs text-muted-foreground">Version {selectedWorkflow.version}</span>
                </div>
                <Separator />
                <h4 className="text-sm font-semibold">Workflow Steps</h4>
                <div className="space-y-3">
                  {selectedWorkflow.steps?.map((step, idx) => (
                    <div key={step.step_number} className="relative pl-8">
                      {idx < selectedWorkflow.steps.length - 1 && (
                        <div className="absolute left-[14px] top-8 bottom-0 w-px bg-border" />
                      )}
                      <div className="absolute left-0 top-1 flex items-center justify-center w-7 h-7 rounded-full bg-[hsl(var(--ring))]/10 text-[hsl(var(--ring))] text-xs font-semibold">
                        {step.step_number}
                      </div>
                      <div className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-150">
                        <div className="text-sm font-medium text-foreground">{step.business_name}</div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>{step.expected_latency_ms}ms expected</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {step.technical_components?.map((comp, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-mono">
                              {comp.type}: {comp.identifier}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ProcessRegistry;
